import { AbstractControl, ValidationErrors } from '@angular/forms';

export function getAgeFromId(idNumber: string): number | null {
  // Basic validation
  if (!idNumber || idNumber.length !== 13 || !/^\d+$/.test(idNumber)) {
    return null;
  }

  // Extract the year, month, and day from the ID number
  const year = idNumber.substring(0, 2);
  const month = idNumber.substring(2, 4);
  const day = idNumber.substring(4, 6);

  // Validate month and day
  const monthNum = parseInt(month);
  const dayNum = parseInt(day);
  if (monthNum < 1 || monthNum > 12 || dayNum < 1 || dayNum > 31) {
    return null;
  }

  // Determine century using current year threshold so IDs like '2807' map
  // to 1928 (for current year 2025) instead of 2028. We compare the
  // two-digit year part to the current year's last two digits.
  const parsedYear = parseInt(year, 10);
  const currentYear = new Date().getFullYear();
  const threshold = currentYear % 100; // e.g. 2025 -> 25

  // If the two-digit year is less than or equal to the current year's
  // last two digits, interpret as 20xx, otherwise 19xx.
  const century = (parsedYear <= threshold) ? 2000 : 1900;

  // Create birthdate and validate it's a real date
  const fullYear = century + parsedYear;
  const birthdate = new Date(fullYear, monthNum - 1, dayNum);

  // Validate that the date components match (JS Date auto-corrects invalid dates)
  if (isNaN(birthdate.getTime()) ||
      birthdate.getFullYear() !== fullYear ||
      birthdate.getMonth() !== monthNum - 1 ||
      birthdate.getDate() !== dayNum) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - birthdate.getFullYear();
  const monthDifference = today.getMonth() - birthdate.getMonth();

  // Check if the birthdate has not occurred yet this year
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthdate.getDate())) {
    age--;
  }

  // Reject future birthdates (birthdate after today)
  if (birthdate.getTime() > today.getTime()) {
    return null;
  }

  // Reject implausible ages (older than 120 years)
  if (age > 120) {
    return null;
  }

  return age;
}

export function nonMinorValidator(
  control: AbstractControl
): ValidationErrors | null {
  if (!control?.value) {
    return null;
  }

  const idNumber = control.value;
  const age = getAgeFromId(idNumber);

  if (age === null) {
    return { invalidIdNumber: true };
  }

  return age < 18 ? { notMinor: true } : null;
}
